<template>

    <ui-link
        class="ui-button" :class="classes" :style="style" v-bind="$componentProps('ui-link')" ref="linkRef"
        :disabled="disabled || loading" :hide-icon="spinnerVisible"
        @click="$emit('click', $event)">

        <template v-if="$slots.icon" v-slot:icon>
            <slot name="icon"></slot>
        </template>

        <ui-spinner v-if="spinnerVisible" visible/>

        <span class="button-content" v-if="$slots.default">
            <slot></slot>
        </span>

    </ui-link>

</template>

<script lang="ts">

import { Component, Prop, Ref, Vue } from 'vue-facing-decorator';

import type { ComponentProps } from '@wittignl/js-core';
import { componentPropsMixin, createComponentMixin } from '@wittignl/js-core';

import Link, { linkCompMixin } from './Link.vue';
import type { LinkComp, LinkProps } from './Link.vue';

import Spinner from '../ui/Spinner.vue';

@Component({
    name: 'ui-button',
    components: {
        'ui-link': Link,
        'ui-spinner': Spinner
    },
    mixins: [componentPropsMixin, linkCompMixin],
    emits: ['click']
})
export default class Button extends Vue {

    @Prop({ type: Boolean })
    active: boolean;

    @Prop({ type: String })
    color: string;

    @Prop({ type: String })
    type: string;

    @Prop({ type: [Boolean, Number] })
    spinner: boolean|number;

    @Prop({ type: [Boolean, Number] })
    loading: boolean|number;

    @Prop({ type: String, default: 'left' })
    iconPosition: string;

    @Prop({ type: Boolean })
    iconFloat: boolean

    @Ref
    linkRef: LinkComp;

    getElement() {

        return this.linkRef.getElement();
    }

    get classes() {

        const hasContent = !!this.$slots.default;
        const hasIcon = this.linkProps.icon || this.$slots.icon;

        let classes = [];

        if(this.color && !this.isVarColor){
            classes.push('color-' + this.color);
        }

        if(this.type){
            classes.push('type-' + this.type);
        }

        if(hasIcon){

            if(hasContent){
                classes.push(`icon-${this.iconPosition}`);
            }
            else {
                classes.push('icon-only');
            }

            if(this.iconFloat){
                classes.push('icon-float')
            }
        }

        if(this.spinnerVisible){

            if(hasIcon){

                if(hasContent){
                    classes.push(`spinner-${this.iconPosition}`);
                }
                else {
                    classes.push('spinner-only');
                }
            }
            else {

                classes.push('spinner-right');
            }
        }

        if(this.linkProps.disabled || this.loading){
            classes.push('disabled');
        }

        if(this.active){
            classes.push('active');
        }

        return classes;
    }

    get style() {

        const style = {};

        if(this.isVarColor){
            style['background-color'] = this.resolvedColor;
        }

        return style;
    }

    get resolvedColor() {

        if(this.$style && this.isVarColor){
            return this.$style.getColor(this.color.substring(1));
        }

        return this.color;
    }

    get isVarColor() {

        return this.color && this.color.charAt(0) === '$';
    }

    get spinnerVisible() {

        return this.spinner || this.loading;
    }

    get linkProps(): LinkProps {
        return this as any
    }
}

export type ButtonComp = InstanceType<typeof Button>
export type ButtonProps = ComponentProps<ButtonComp> & LinkProps
export const buttonCompMixin = createComponentMixin(Button, linkCompMixin)

</script>
<style src="../../../scss/component/button/Button.scss" lang="scss"></style>